import type { AdminUser, FlagKey } from '../types';
import { Icon, Avatar, fmt } from '../atoms';
import { FLAGS } from '../data';

function metricFor(u: AdminUser, f: FlagKey): { v: string; l: string } {
  if (f === 'rate') return { v: fmt.int(u.reqs), l: 'requests' };
  if (f === 'cost') return { v: '×' + (u.baseline ? (u.cost / u.baseline).toFixed(1) : '—'), l: 'vs baseline' };
  if (f === 'cap') return { v: fmt.pct(u.tokens, u.capTokens) + '%', l: 'of token cap' };
  if (f === 'storage') return { v: fmt.mb(u.db + u.s3), l: 'total' };
  if (f === 'logins') return { v: String(u.fails), l: 'failed logins' };
  return { v: 'review', l: '' };
}

export function Misuse({ users, onOpen, act }: {
  users: AdminUser[];
  onOpen: (u: AdminUser) => void;
  act: (type: string, user: AdminUser) => void;
}) {
  const flagged = users.filter(u => u.flags.length > 0);
  const byType: Partial<Record<FlagKey, AdminUser[]>> = {};
  (Object.keys(FLAGS) as FlagKey[]).forEach(k => { byType[k] = users.filter(u => u.flags.includes(k)); });

  return (
    <>
      <div className="kpis">
        <div className="card kpi">
          <div className="k-h"><span className="ic"><Icon name="alert" size={14} /></span>Flagged accounts</div>
          <div className="k-v">{flagged.length}</div>
          <div className="k-sub"><span>of {users.length} monitored</span></div>
        </div>
        <div className="card kpi">
          <div className="k-h"><span className="ic"><Icon name="bolt" size={14} /></span>High severity</div>
          <div className="k-v">{flagged.filter(u => u.flags.some(f => FLAGS[f].sev === 'bad')).length}</div>
          <div className="k-sub"><span>rate / logins / dupes</span></div>
        </div>
        <div className="card kpi">
          <div className="k-h"><span className="ic"><Icon name="coin" size={14} /></span>At / over AI cap</div>
          <div className="k-v">{users.filter(u => fmt.pct(u.cost, u.capCost) >= 100 || fmt.pct(u.tokens, u.capTokens) >= 100).length}</div>
          <div className="k-sub"><span>AI auto-paused</span></div>
        </div>
        <div className="card kpi">
          <div className="k-h"><span className="ic"><Icon name="fingerprint" size={14} /></span>Duplicate signals</div>
          <div className="k-v">{byType.dupe?.length ?? 0}</div>
          <div className="k-sub"><span>pending / active</span></div>
        </div>
      </div>

      <div className="sec-h"><h2>Flagged accounts</h2><span className="c">newest signals first</span></div>

      {flagged.length ? (
        <div className="stack">
          {flagged.map(u => {
            const f = u.flags[0], m = FLAGS[f], mt = metricFor(u, f);
            return (
              <div className={'fcard' + (m.sev === 'bad' ? ' bad' : '')} key={u.id}>
                <Avatar size={40} hue={u.hue} initials={u.initials} dis={u.status === 'disabled'} />
                <div className="who" style={{ cursor: 'pointer' }} onClick={() => onOpen(u)}>
                  <div className="n">
                    {u.name}{' '}
                    {u.flags.map(fl => (
                      <span key={fl} className={'badge ' + FLAGS[fl].sev} style={{ marginLeft: 6 }}>
                        <span className="d" />{FLAGS[fl].label}
                      </span>
                    ))}
                  </div>
                  <div className="desc">{m.desc}</div>
                </div>
                <div className="metric"><div className="v">{mt.v}</div><div className="l">{mt.l}</div></div>
                <button className="btn sm" onClick={() => act('investigate', u)}>
                  <Icon name="eye" size={14} />Investigate
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--soft)' }}>
          No active misuse signals.
        </div>
      )}
    </>
  );
}
